
import { Directive, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { FormWidgetService } from 'common/widget/form-widget.service';
import { Validation, navInfo } from 'common/models/utility-models/utility-interface';
import { DataModel, RouteEnvelope, WidgetModel } from 'common/models/utility-models/base-model';
import { SystemService } from 'common/services/core-services/system.service';
import { OperationType, ROUTE_PATH } from 'common/constants/sm-constants';
import { FormMetaModel } from 'common/models/utility-models/meta-model';
import { GridParams } from 'common/models/utility-models/list-utility.model';

@Directive()
export class BaseFormComponent<T extends DataModel> implements OnInit {
    pageData!: T;
    pageMeta!: FormMetaModel;
    pageWidget!: WidgetModel;
    initForm: FormGroup = new FormGroup({});
    verifyOtpScreen: boolean = false;
    afterSubmitScreen: boolean = false;


    constructor(
        protected activatedRoute: ActivatedRoute,
        protected widgetService: FormWidgetService<T>,
        protected systemService: SystemService
    ) { }

    ngOnInit(): void {
        this.pageData = this.activatedRoute.snapshot.data[0]['data'];
        this.pageMeta = this.activatedRoute.snapshot.data[0]['meta'];
        this.pageWidget = this.activatedRoute.snapshot.data[0]['widget'];
        if (this.pageMeta.properties?.isStepForm) {
            this.generateStepForm(this.pageData);
        } else {
            this.generateForm(this.pageData, this.pageMeta, this.initForm);
        }
    }

    generateForm(formData: any, pageMeta: FormMetaModel | any = this.pageMeta, formGroup: FormGroup | any = this.initForm): FormGroup {
        const keyData = Object.keys(formData).filter((element: any) => element !== 'id');
        keyData.forEach((element: any) => {
            this.addFormControl(element, pageMeta, formGroup);
        });
        this.patchValues();
        return formGroup;

    }

    addFormControl(element: any, pageMeta: FormMetaModel | any, formGroup: FormGroup | any): void {
        formGroup.addControl(element, new FormControl({ value: '', disabled: false }));
        if (pageMeta.properties[element] !== undefined) {
            this.addFieldValidation(element, pageMeta.properties[element], formGroup);
        }
    }

    patchValues(): void {
        const keys = Object.keys(this.pageData).filter((key: any) => key !== 'id');
        keys.forEach((field: any) => {
            if (this.pageMeta.properties[field] !== undefined) {
                if (this.pageMeta.properties[field]['isFormControl'] === true) {
                    this.initForm.patchValue({
                        [field]: this.pageData[field]
                    });
                }
            }
        });
    }

    validateControls(controls: any[]): boolean {
        const valid =
            controls.filter((control: FormControl) => {
                return control.valid;
            }).length === controls.length;
        if (!valid) {
            controls.forEach((control: FormControl) => {
                control.markAsTouched();
            });
        }
        return valid;

    }

    addFieldValidation(fieldName: string, fieldProp: any, formGroup: FormGroup) {
        const Validation: ValidatorFn[] = [];
        const fieldControl = formGroup.controls[fieldName];
        if (fieldProp.fieldType !== 'dynamicform') {
            const validationProps: Validation = {
                required: fieldProp.required,
                type: fieldProp.type,
                maxLength: fieldProp.maxlength,
                minLength: fieldProp.minlength,
                pattern: fieldProp.pattern
            };


            if (!fieldControl) {
                console.error(`Field '${fieldName}' not found in the form.`);
                return;
            }
            if (validationProps.required) {
                Validation.push(Validators.required);
            }
            if (validationProps.minLength) {
                Validation.push(Validators.minLength(validationProps.minLength));
            }
            if (validationProps.maxLength) {
                Validation.push(Validators.maxLength(validationProps.maxLength));
            }
            if (validationProps.pattern) {
                Validation.push(Validators.pattern(validationProps.pattern));
            }
        } else {
            if (fieldProp.proprequired) {
                Validation.push(Validators.required);
            }
        }

        formGroup.controls[fieldName].setValidators(Validation);
        formGroup.controls[fieldName].updateValueAndValidity()

    }

    rowNavigationAction(event: navInfo) {
        const envelope = new RouteEnvelope();
        let url;
        envelope.type = event.navigateOperationType;
        envelope.transferData = event.navigateTransferData;
        url = event.navigationUrl
        this.navigatePage(url, envelope)
    }

    onSubmit(event: any, method: 'get' | 'post' = 'post') {
        method === 'post' ? this.processForm() : this.processFormGETAPI();
    }

    processForm() {


        if (!this.initForm.valid) {
            this.initForm.markAllAsTouched();
            return;
        }
        if (this.pageData.id !== null && this.pageData.id !== 0 && this.checkUpdatedForm() === 0) {
            this.systemService.showError('No changes done');
            return;
        }
        const model = this.getFormModel();

        if (this.validate(model)) {

            this.widgetService
                .processFormModel(model, this.pageMeta, this.pageData)
                .then(result => {
                    if (result !== null) {

                        this.navigatePage(this.pageMeta.redirectPath, this.routeMessage(), result)

                    }

                })
                .catch(err => {
                    this.handleError(err);

                });
        } else {
            this.verifyOtpScreen = false
            this.systemService.showError(
                this.pageMeta.properties.formInfo.errorMessage !== null
                    ? this.pageMeta.properties.formInfo.errorMessage
                    : 'Invalid'
            );
        }
    }


    processFormGETAPI() {
        if (!this.initForm.valid) {
            this.initForm.markAllAsTouched();
            return;
        }
        this.widgetService
            .processFormModelGETAPI(this.createGETParams())
            .then(result => {

                this.navigatePage(this.pageMeta.redirectPath, this.routeMessage(), result);
            })
            .catch(error => {
                this.handleError(error);
            });
    }

    createGETParams(): string[] {
        return [];
    }

    validate(model: any) {
        return this.widgetService.validationBeforeSubmit(model);
    }

    routeMessage(): RouteEnvelope {
        const message = new RouteEnvelope();
        message.type = OperationType.VIEW;
        message.transferData = new GridParams();
        return message;
    }

    navigatePage(route: any, dataRes: any = new RouteEnvelope(), apiRes: any = null) {
        const message = dataRes;
        this.systemService.navigateByUrl(route, message);
    }




    protected async refreshData(): Promise<void> {
        const params: any = this.createQueryParams();
        this.widgetService
            .getDataById(params)
            .then((data: any) => {
                this.pageData = data
                // this.prepareData(data, this.listQueryParams)
            })
            .catch((e: any) => this.handleError(e));
    }

    createQueryParams() {
        return {};
    }


    getFormModel() {
        const model = this.initForm.value;
        if (this.pageData.id !== 0) {
            model['id'] = this.pageData.id;
        }
        return model;
    }

    modifyFieldParam(event) {

    }

    checkUpdatedForm() {
        let count = 0;
        let condition = this.addOnEditCondition();
        Object.keys(this.initForm.value).forEach((key: string) => {
            if (this.pageMeta.properties[key] !== undefined &&
                this.pageMeta.properties[key].isFormControl !== false &&
                this.pageData[key] !== this.initForm.value[key]
            ) {
                count++;
            }
            if (condition) {
                count++;
            }
        });
        return count;
    }

    addOnEditCondition() {
        return false;
    }

    handleError(err: Error) { }


    generateStepForm(formData: any) {
        const keyData = Object.keys(formData).filter((element: any) => element !== 'id' && element !== 'isStepForm');

        keyData.forEach((element: any) => {
            this.initForm.addControl(element, new FormGroup({}));
            this.generateForm(this.pageData[element], this.pageMeta.properties[element], this.initForm.get(element))
        });
    }

    resetForm() {
        this.initForm.reset();
        this.initForm.markAsUntouched();
        this.initForm.markAsPristine();
        // this.isReset = true;
    }


    privacyNoticeNavigation() {
        const envelope = new RouteEnvelope();
        envelope.type = OperationType.VIEW;
        envelope.transferData = new WidgetModel();
        this.systemService.navigateByUrl(ROUTE_PATH.privacy, envelope);
    }
}
